import type { Drone } from '@/types/fleet';
import type { Dock } from '@/types/dock';
import type { DispatchDroneOption } from '@/components/dashboard/QuickDispatchDialog';

/**
 * Just enough of a live track to name it and to say whether it is UP.
 *
 * `status` matters because `drones.dock_id` is the aircraft's HOME dock and stays
 * set the whole time it is flying. Labelling from `dock_id` alone therefore tagged
 * an aircraft at 400 ft as "in dock M-011" - and that tag is the operator's only
 * cue that picking a row opens a bay and starts an engine. The server does not
 * make this mistake: `resolveLaunchDock` asks `isAircraftAirborne` and takes the
 * airborne branch. This is the client agreeing with it.
 */
type LiveTrack = { drone_id: string; callsign?: string; status?: string };

/** Just enough of a dock row to name it. */
type NamedDock = Pick<Dock, 'id' | 'name' | 'callsign'>;

/**
 * The aircraft Quick Dispatch can actually launch: everything live on the map,
 * PLUS every aircraft stationed in a bay.
 *
 * WHY THIS EXISTS. The COP's live drone map is built purely from telemetry
 * frames (`useWebSocket`), and a docked drone is powered OFF - the dock only
 * turns it on when the launch sequence sends `pre_takeoff`. It therefore
 * publishes nothing, never enters that map, and the dispatch picker hid
 * precisely the fleet a dock exists to hold. The server never had this limit:
 * `launchMissionNow` resolves `drone.dock_id` and runs the full dock sequence,
 * so this was the UI withholding a launch the API would have accepted.
 *
 * Strictly additive. Every aircraft that was listed before is still listed, and
 * still FIRST - one already airborne is the fastest answer to an incident.
 */
export function dispatchableDrones(
  live: readonly LiveTrack[],
  registry: ReadonlyMap<string, Drone>,
  docks: readonly NamedDock[],
  siteId: string | null,
): DispatchDroneOption[] {
  const dockOf = (droneId: string): { name: string | null } | null => {
    const dockId = registry.get(droneId)?.dock_id;
    if (!dockId) return null;
    const dock = docks.find((k) => k.id === dockId);
    // Two different unknowns, and collapsing them printed a placeholder into an
    // operator-facing sentence about opening a bay ("Launch from dock unknown").
    // `null` here means NOT in a bay. `{ name: null }` means in a bay we cannot
    // name yet - `dockRegistry` is its own unsequenced fetch, so that is every
    // cold load where /api/drones answers first, and the whole session if
    // /api/docks fails. Still stationed either way; the copy just stops naming it.
    // The word "dock" belongs to the copy, not the value - these docks are called
    // things like `M-011`, which alone reads as a site or a zone just as easily.
    return { name: dock?.callsign ?? dock?.name ?? null };
  };

  // `live` is already site-filtered by the caller (copDrones), so it is passed
  // through untouched - re-filtering it here on the registry would DROP a live
  // aircraft whose registry row has not loaded, which is a regression on a list
  // this change is only supposed to widen.
  const airborne = live.map((d) => ({
    id: d.drone_id,
    label: (d.callsign || d.drone_id.slice(-6)).toUpperCase(),
    // An aircraft that is FLYING is not in its bay, whatever `dock_id` still says.
    // `status` is useWebSocket's own per-frame derivation (altitude > 2 m), the
    // same signal the COP's `airborneDrones` map uses.
    dock: d.status === 'flying' ? null : dockOf(d.drone_id),
  }));

  const listed = new Set(airborne.map((d) => d.id));
  const docked = Array.from(registry.values())
    .filter((d) => d.dock_id !== null && !listed.has(d.id))
    // The same site rule copDrones gets from useSiteDroneFilter, read off the
    // registry row that filter is itself derived from.
    .filter((d) => !siteId || d.site_id === siteId)
    .map((d) => ({
      id: d.id,
      label: (d.callsign || d.name || d.id.slice(-6)).toUpperCase(),
      dock: dockOf(d.id),
    }));

  return [...airborne, ...docked];
}
